/*
 * CALENDAR-DAY ARITHMETIC, with no time zone in it.
 *
 * A start or due date is a DAY on the Europe/Ljubljana calendar ("YYYY-MM-DD"), not an
 * instant (docs/API.md). Doing sums on Date objects in the browser's zone would move a day
 * across midnight twice a year when the clocks change, so every day here is turned into a
 * plain integer — days since 1970-01-01, computed in UTC where no day is 23 or 25 hours
 * long — and back. The Gantt, the Kanban and the lists all count with these.
 */

const MS_PER_DAY = 86_400_000

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/** Whether a value is a "YYYY-MM-DD" day the API would accept. */
export const isDay = (value: unknown): value is string => typeof value === 'string' && DAY_PATTERN.test(value)

/** "2026-09-23" → days since 1970-01-01. */
export const dayNumber = (day: string): number => {
    const [year = 1970, month = 1, date = 1] = day.split('-').map(Number)

    return Math.round(Date.UTC(year, month - 1, date) / MS_PER_DAY)
}

/** Days since 1970-01-01 → "2026-09-23". */
export const dayString = (value: number): string => new Date(value * MS_PER_DAY).toISOString().slice(0, 10)

/** 0 = Monday … 6 = Sunday, the Slovenian week. */
export const weekdayOf = (value: number): number => (new Date(value * MS_PER_DAY).getUTCDay() + 6) % 7

export const isWeekend = (value: number): boolean => weekdayOf(value) >= 5

/** The Monday of the week a day falls in. */
export const mondayOf = (value: number): number => value - weekdayOf(value)

/** Year, month (1–12) and day of the month of a day number. */
export const dayParts = (value: number): { year: number, month: number, date: number } => {
    const at = new Date(value * MS_PER_DAY)

    return { year: at.getUTCFullYear(), month: at.getUTCMonth() + 1, date: at.getUTCDate() }
}

/** The first day of the month a day falls in. */
export const monthStartOf = (value: number): number => {
    const { year, month } = dayParts(value)

    return Math.round(Date.UTC(year, month - 1, 1) / MS_PER_DAY)
}

/** The first day of the NEXT month. */
export const nextMonthStart = (value: number): number => {
    const { year, month } = dayParts(value)

    return Math.round(Date.UTC(year, month, 1) / MS_PER_DAY)
}

export const nextYearStart = (value: number): number => Math.round(Date.UTC(dayParts(value).year + 1, 0, 1) / MS_PER_DAY)

/** A day number as a UTC Date, for Intl formatting with `timeZone: 'UTC'`. */
export const dayAsDate = (value: number): Date => new Date(value * MS_PER_DAY)

/*
 * TODAY, IN LJUBLJANA — the calendar the server counts overdue and due-today by, whatever
 * zone the browser happens to be in. The API's own `today` wins wherever an answer carries
 * one (the dashboard, the people timeline); this is for the screens whose answer does not.
 */
export const todayInLjubljana = (): string => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Ljubljana',
        year:     'numeric',
        month:    '2-digit',
        day:      '2-digit',
    }).formatToParts(new Date())

    const part = (type: string): string => parts.find((item) => item.type === type)?.value ?? ''

    return `${part('year')}-${part('month')}-${part('day')}`
}

/** The inclusive span of a task on the calendar, or null when it has no date at all. */
export const taskSpan = (start: string | null, due: string | null): { start: number, end: number } | null => {
    const from = start || due
    const to   = due || start

    if (!from || !to) return null

    const first = dayNumber(from)
    const last  = dayNumber(to)

    return { start: Math.min(first, last), end: Math.max(first, last) }
}
